/* oxlint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/browser';
import type { GraphQLFormattedError } from 'graphql';

import { ClientGraphQLError, FatalError } from '..';
import type { ClientError, ErrorOptions, ErrorToastConfig } from '../../types';
import { isHtmlResponseError, printError, shouldThrowError } from '../helpers';

// BE contract (see source/go/lib/graphql/gqlutil/gqlutil.go `hadServerError`):
// every GraphQL response is HTTP 200; per-error severity lives in
// `extensions.errorCode`. Codes >= 500 are server bugs.
//
// Inside the < 500 bucket the BE conflates two categories we want to treat
// differently on the FE:
//   - 4xx > 400 (401/403/404/409/422/...): business rules / auth-expected /
//     user-visible validation. Pure noise in Sentry — toast the user, skip
//     Sentry capture entirely.
//   - 400: BE uses this for both "user failed validation" AND "FE sent a
//     malformed request" (missing variable, wrong scalar type, stale ID).
//     The second category is a real FE bug, so we keep 400 going to Sentry
//     at default error severity until the BE separates the two semantics.
const isBusinessRuleGQLErrorCode = (errorCode: unknown): boolean =>
  typeof errorCode === 'number' && errorCode > 400 && errorCode < 500;

// Apollo v4 replaced v3's `ApolloError` with `CombinedGraphQLErrors`, an
// `Error` subclass whose `errors` array carries the raw GraphQL errors. The
// Apollo `ErrorLink` (packages/graphql `createApolloClient`) unwraps it and
// calls handleError once per member error, so each one takes the `isGQLError`
// branch below and the business-rule filter applies. Callers that hold the
// aggregate pass it here whole instead — most notably `QueryErrorBoundary`'s
// `error` prop — and because the aggregate *is* an `Error`, `isGQLError` is
// false and the filter never runs. That let 4xx business-rule rejections
// through as default-severity issues, double-reported alongside the link's
// already-filtered copy (BACKOFFICE-YY: a 404 for an operation ID that
// doesn't exist, escalating off the operation detail page).
//
// Duck-typed rather than `CombinedGraphQLErrors.is`: `@apollo/client` is only
// a devDependency here, and `@anchorage/graphql` already imports
// `handleError`, so importing it back would be circular.
const getAggregatedGQLErrors = (
  error: ClientError,
): GraphQLFormattedError[] | undefined => {
  const errors = (error as { errors?: unknown }).errors;
  // An empty array must not count: `every` is vacuously true, which would
  // silently drop an aggregate carrying no member errors.
  if (!Array.isArray(errors) || errors.length === 0) {
    return undefined;
  }
  return errors as GraphQLFormattedError[];
};

// Error Notifier integration
// This function should be set by the app at runtime, e.g. in a top-level provider.
// It receives the full toast config (title/description/variant/action/etc.) plus
// the Sentry event ID for feedback linking.
export type NotifyErrorOptions = ErrorToastConfig & {
  associatedEventID?: string;
};

export let notifyError: ((opts: NotifyErrorOptions) => void) | undefined;

// A function to set the error notifier
// This allows the app to provide a custom error notification mechanism, such as a toast
// Hooks couldn't be used here because error handling can be called by non-react components
export const setErrorNotifier = (fn: (opts: NotifyErrorOptions) => void) => {
  notifyError = fn;
};

// Both business-rule branches below end identically: the rejection is an
// intentional product signal, so toast the user and return without a Sentry
// capture. The empty event ID signals there is nothing for feedback to link to.
const notifyBusinessRuleRejection = (opts?: ErrorOptions): string => {
  if (notifyError && opts?.toastConfig) {
    notifyError({ ...opts.toastConfig });
  }
  return '';
};

/**
 * setErrorScope adds custom key value pairs to the scope of the error. This
 * allows us to send extra data to Sentry for debugging
 * @param extraKeyValuePairs
 */
const setErrorScope = (extraKeyValuePairs: { [key: string]: any }): void => {
  Sentry.getCurrentScope().setExtras(extraKeyValuePairs);
};

/**
 * setLevel configures the scope of the reported exception to use a level other
 * than the default 'error' level. 'fatal' will page the on-call engineer.
 * @param level severity of the captured exception
 */
const setLevel = (level: Sentry.SeverityLevel): void => {
  Sentry.getCurrentScope().setLevel(level);
};

/**
 * handleError handles errors that are thrown in the application, it:
 * - Filters and categorizes out inactionable errors
 * - Adds custom key value pairs to the scope of the error
 * - Reports to Sentry
 * - Logs the error
 * @param error
 * @param opts
 */
export const handleError = (
  error: ClientError,
  opts?: ErrorOptions,
): string => {
  if (!error) {
    return '';
  }

  // Business-rule rejections carried inside an Apollo aggregate. Mirrors the
  // single-error branch below: toast the user, keep Sentry clean. Requires
  // *every* member to be a 4xx > 400 product signal, so a mixed aggregate
  // (any member at 400, or >= 500) still reports and a real bug can't be
  // masked by a business-rule sibling.
  const aggregatedErrors = getAggregatedGQLErrors(error);
  if (
    aggregatedErrors?.every((gqlError) =>
      isBusinessRuleGQLErrorCode(gqlError?.extensions?.errorCode),
    )
  ) {
    return notifyBusinessRuleRejection(opts);
  }

  let errorToReport: { [key: string]: any } = error;

  const isGQLError = !(error instanceof Error) && error.message && error.path;
  if (isGQLError) {
    const errorCode = error.extensions?.errorCode;

    // Business-rule rejections (4xx > 400) are intentional product signals
    // — toast the user, but don't pollute Sentry. The bulk of the noise.
    // 400 is excluded because the BE also uses it for malformed-request
    // bugs we *do* want to see.
    if (isBusinessRuleGQLErrorCode(errorCode)) {
      return notifyBusinessRuleRejection(opts);
    }

    // Skip this when the caller opted out of reporting: `setExtras` writes to
    // the live current scope (not a fork), so anything set here would still
    // be attached to a later, unrelated event captured on the same scope —
    // defeating the purpose of skipErrorReport for sensitive variables.
    if (!opts?.skipErrorReport) {
      setErrorScope({
        GQLOperationName: opts?.operationName,
        GQLVariables: opts?.operationVariables,
      });
    }

    // Create an error object from the plain object given to us by Apollo
    errorToReport = new ClientGraphQLError(error, opts);

    // Strip the ClientGraphQLError constructor *and* this handleError wrapper
    // from the stack so Sentry's "most relevant frame" is the actual caller
    // (Apollo onError link / component / hook) rather than handleError.ts.
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(errorToReport as Error, handleError);
    }
  }

  // Do not report errors where the server sent back HTML instead of valid JSON
  // — the client is logged out, the load balancer returned a 502, the backend
  // returned a 408. Not actionable. The name check alone is not enough: Apollo
  // only names it `ServerParseError` below status 300, and throws a plain
  // `ServerError` at 300+ without attempting the parse, so match the HTML body
  // too (see isHtmlResponseError). Live issue: BACKOFFICE-YR (TTRM-4967).
  if (
    errorToReport.name === 'ServerParseError' ||
    isHtmlResponseError(errorToReport)
  ) {
    return '';
  }

  // These will call the on-call engineer
  if (errorToReport instanceof FatalError) {
    setLevel('fatal');
  } else if (opts?.level) {
    setLevel(opts.level);
  }

  // Same live-scope contamination risk as the GQLOperationName/GQLVariables
  // skip above: errorToReport (ClientGraphQLError) carries the raw GraphQL
  // error on `cause`, which can hold PII the caller opted out of reporting.
  if (!opts?.skipErrorReport) {
    setErrorScope(errorToReport);
  }

  const extra: Record<string, unknown> = {};
  // Network-layer errors carry no GraphQL error body, so the operation name is
  // the only thing identifying which request failed. It rides the capture hint
  // rather than the live scope because `beforeSend` can still drop this event
  // (an ignored status, say), and a live-scope write would then resurface on
  // the next unrelated event.
  if (!isGQLError && opts?.operationName) {
    extra.GQLOperationName = opts.operationName;
  }
  if (opts?.orgID) {
    extra.orgID = opts.orgID;
  }
  if (opts?.extraContext) {
    Object.assign(extra, opts.extraContext);
  }
  const hint = Object.keys(extra).length ? { extra } : undefined;

  // Store the sentry event ID for the error
  // This can be used by feedback forms to associate the error with user feedback
  let eventID: string = '';

  // Do not report the error to Sentry if the caller opted out via
  // `skipErrorReport` (the error is expected and non-actionable — e.g. a
  // permission probe the BE answers with an error).
  if (!opts?.skipErrorReport) {
    if (errorToReport instanceof Error) {
      // Add each custom field to the error's scope so that the reports have
      // access to the custom fields
      if (hint) {
        eventID = Sentry.captureException(errorToReport, hint);
      } else {
        eventID = Sentry.captureException(errorToReport);
      }
    } else {
      // Call "captureEvent" instead of "captureException" so that Sentry does not
      // capture it as "Non-Error exception captured"
      eventID = Sentry.captureEvent(errorToReport);
    }
  }

  printError(error);

  // Error Notifier: show toast if toastConfig is provided. Full
  // ErrorToastConfig is forwarded — title/description/variant/action all
  // reach the toast system.
  if (notifyError && opts?.toastConfig) {
    notifyError({ ...opts.toastConfig, associatedEventID: eventID });
  }

  if (shouldThrowError(error, opts)) {
    throw error;
  }

  return eventID;
};
